import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowClockwise, PaperPlaneTilt, X as XIcon, Sparkle, CheckCircle,
  Clock, EnvelopeSimple, EyeSlash, Play, Lightning, ChartBar, Eye, CursorClick, Target,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STEP_LABELS = {
  load:      { title: "Add first load",   short: "Load",      color: "text-orange-400"  },
  gmail:     { title: "Connect Gmail",    short: "Gmail",     color: "text-blue-400"    },
  pricing:   { title: "Rate rules",       short: "Pricing",   color: "text-purple-400"  },
  negotiate: { title: "AI Rate Negotiator", short: "Negotiate", color: "text-emerald-400" },
  driver:    { title: "Add driver",       short: "Driver",    color: "text-pink-400"    },
};

const TONE_OPTIONS = [
  { value: "warm",   label: "Warm",   desc: "Personal, like a fellow trucker" },
  { value: "direct", label: "Direct", desc: "Cut to the value" },
  { value: "urgent", label: "Urgent", desc: "Trial-clock urgency" },
];

function fmtHours(h) {
  if (h == null) return "—";
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

function ComposeModal({ open, candidate, onClose, onSent, adminEmail }) {
  const [tone, setTone] = useState("warm");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [replyTo, setReplyTo] = useState(adminEmail || "");

  useEffect(() => {
    if (open) {
      setDraft(null);
      setTone("warm");
      setReplyTo(adminEmail || "");
    }
  }, [open, adminEmail]);

  if (!open || !candidate) return null;

  const generate = async () => {
    setDrafting(true);
    try {
      const { data } = await client.post("/admin/activation/draft", {
        user_id: candidate.user.id,
        step_key: candidate.step_key,
        tone,
      });
      setDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!draft?.subject || !draft?.body) {
      toast.error("Generate a draft first");
      return;
    }
    setSending(true);
    try {
      const { data } = await client.post("/admin/activation/send", {
        user_id: candidate.user.id,
        step_key: candidate.step_key,
        subject: draft.subject,
        body: draft.body,
        tone,
        reply_to: replyTo || undefined,
      });
      if (data?.status === "sent") {
        const via = data.provider === "gmail_smtp" ? " (via your Gmail)" :
                    data.provider === "resend" ? " (via Resend)" : "";
        toast.success(`Sent to ${candidate.user.email}${via}`);
        onSent?.();
        onClose();
      } else {
        toast.error(data?.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const stepInfo = STEP_LABELS[candidate.step_key] || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="activation-compose-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                <Lightning size={12} weight="fill" /> Activation Nudge
              </div>
              <div className="text-lg font-semibold text-white">
                {candidate.user.full_name || candidate.user.email}
              </div>
              <div className="text-xs text-neutral-400 mt-0.5">
                {candidate.user.email} • Signed up {fmtHours(candidate.user.hours_since_signup)} ago
              </div>
              <div className="text-xs mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.05] ${stepInfo.color}`}>
                  Stuck on: {stepInfo.title}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white"
                    data-testid="activation-modal-close">
              <XIcon size={18} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* Tone selector */}
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Tone</div>
              <div className="grid grid-cols-3 gap-2">
                {TONE_OPTIONS.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setTone(t.value)}
                    data-testid={`activation-tone-${t.value}`}
                    className={`text-left p-3 rounded-lg border transition-colors ${
                      tone === t.value
                        ? "border-[#FF5A1F] bg-[#FF5A1F]/[0.08]"
                        : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <div className={`text-sm font-medium ${tone === t.value ? "text-[#FF5A1F]" : "text-white"}`}>
                      {t.label}
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={generate}
              disabled={drafting}
              data-testid="activation-generate-btn"
              className="w-full btn-primary rounded-full py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Sparkle size={14} weight="fill" /> {drafting ? "Drafting with GPT-5.2..." : (draft ? "Regenerate draft" : "Generate draft")}
            </button>

            {draft && (
              <>
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Subject</div>
                  <input
                    type="text"
                    value={draft.subject}
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    data-testid="activation-subject-input"
                  />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Body</div>
                  <textarea
                    rows={12}
                    value={draft.body}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
                    data-testid="activation-body-textarea"
                  />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Reply-To (optional)</div>
                  <input
                    type="email"
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    placeholder="admin@freightpilotai.ai"
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    data-testid="activation-replyto-input"
                  />
                </div>
              </>
            )}
          </div>

          <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between gap-3">
            <button
              onClick={onClose}
              className="text-sm text-neutral-500 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={!draft || sending}
              data-testid="activation-send-btn"
              className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <PaperPlaneTilt size={14} weight="fill" /> {sending ? "Sending..." : "Send activation email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelBar({ label, value, total, color, testid, icon: Icon }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-neutral-500">
          {Icon && <Icon size={12} />} {label}
        </div>
        <div className="text-xs text-neutral-400">
          <span className="text-white font-semibold">{value}</span>
          {total > 0 && <span className="text-neutral-600 ml-1.5">({pct.toFixed(1)}%)</span>}
        </div>
      </div>
      <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${Math.max(pct, value > 0 ? 2 : 0)}%` }}
        />
      </div>
    </div>
  );
}

function ActivationAnalytics({ data, days, onDaysChange }) {
  const o = data?.overall || {};
  const perStep = data?.per_step || {};
  const perTone = data?.per_tone || {};

  // Find winning tone (highest activation rate with at least 1 sent)
  const toneEntries = Object.entries(perTone).filter(([_, v]) => v.sent > 0);
  const winningTone = toneEntries.length > 1
    ? toneEntries.reduce((best, cur) => cur[1].activation_rate > best[1].activation_rate ? cur : best)[0]
    : null;

  return (
    <div className="card-surface p-5 mb-6" data-testid="activation-analytics">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ChartBar size={16} className="text-[#FF5A1F]" weight="fill" />
          <div className="text-sm font-semibold text-white">Activation Funnel</div>
          <div className="text-xs text-neutral-500">— Last {days} days</div>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-white/10 p-0.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => onDaysChange(d)}
              data-testid={`activation-analytics-range-${d}`}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                days === d ? "bg-[#FF5A1F] text-black font-semibold" : "text-neutral-400 hover:text-white"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {o.sent === 0 ? (
        <div className="py-8 text-center text-sm text-neutral-500">
          <div className="text-white font-medium mb-1">No activation emails in this window yet</div>
          Send your first batch to see the funnel + A/B tone comparison.
        </div>
      ) : (
        <>
          {/* Funnel bars */}
          <div className="space-y-3.5" data-testid="activation-analytics-funnel">
            <FunnelBar label="Sent" value={o.sent} total={o.sent} color="bg-neutral-500" icon={PaperPlaneTilt} testid="funnel-sent" />
            <FunnelBar label="Opened" value={o.opened} total={o.sent} color="bg-blue-400" icon={Eye} testid="funnel-opened" />
            <FunnelBar label="Clicked" value={o.clicked} total={o.sent} color="bg-purple-400" icon={CursorClick} testid="funnel-clicked" />
            <FunnelBar label="Activated" value={o.activated} total={o.sent} color="bg-[#FF5A1F]" icon={Target} testid="funnel-activated" />
          </div>

          {/* Key summary line */}
          <div className="mt-5 pt-4 border-t border-white/5 text-sm text-neutral-400">
            Of the <span className="text-white font-semibold">{o.sent}</span> emails sent,
            <span className="text-[#FF5A1F] font-semibold"> {o.activated} activated</span>
            <span className="text-neutral-600"> ({o.activation_rate}% activation rate)</span>.
            {winningTone && (
              <span className="ml-2 text-neutral-500">
                Winning tone: <span className="text-emerald-400 font-semibold capitalize">{winningTone}</span>
              </span>
            )}
          </div>

          {/* Breakdown grids */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {/* Per step */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-3">By Step</div>
              <div className="space-y-2">
                {Object.entries(perStep).map(([step, m]) => {
                  const info = STEP_LABELS[step] || {};
                  return (
                    <div key={step}
                         className="flex items-center gap-3 text-xs"
                         data-testid={`activation-analytics-step-${step}`}>
                      <div className={`w-20 truncate ${info.color}`}>{info.short}</div>
                      <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                        <div
                          className="h-full bg-[#FF5A1F]/70 rounded-full"
                          style={{ width: `${m.sent > 0 ? m.activation_rate : 0}%` }}
                        />
                      </div>
                      <div className="w-24 text-right text-neutral-400 tabular-nums">
                        {m.activated}/{m.sent} · <span className={m.sent > 0 ? "text-white font-semibold" : "text-neutral-600"}>{m.activation_rate}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Per tone */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-3">By Tone (A/B)</div>
              <div className="space-y-2">
                {Object.entries(perTone).map(([tone, m]) => {
                  const isWinner = tone === winningTone;
                  return (
                    <div key={tone}
                         className="flex items-center gap-3 text-xs"
                         data-testid={`activation-analytics-tone-${tone}`}>
                      <div className={`w-20 truncate capitalize ${isWinner ? "text-emerald-400 font-semibold" : "text-neutral-300"}`}>
                        {tone}{isWinner && " ★"}
                      </div>
                      <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                        <div
                          className={`h-full rounded-full ${isWinner ? "bg-emerald-400" : "bg-purple-400/60"}`}
                          style={{ width: `${m.sent > 0 ? m.activation_rate : 0}%` }}
                        />
                      </div>
                      <div className="w-24 text-right text-neutral-400 tabular-nums">
                        {m.activated}/{m.sent} · <span className={m.sent > 0 ? "text-white font-semibold" : "text-neutral-600"}>{m.activation_rate}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminActivationPage() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [perStep, setPerStep] = useState({});
  const [total, setTotal] = useState(0);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState("due");
  const [modal, setModal] = useState({ open: false, candidate: null });

  const load = async () => {
    setLoading(true);
    try {
      const [c, h, a] = await Promise.all([
        client.get("/admin/activation/candidates"),
        client.get("/admin/activation/history"),
        client.get(`/admin/activation/analytics?days=${analyticsDays}`),
      ]);
      setCandidates(c.data?.candidates || []);
      setPerStep(c.data?.per_step || {});
      setTotal(c.data?.total || 0);
      setHistory(h.data?.emails || []);
      setAnalytics(a.data || null);
    } catch (e) {
      toast.error("Could not load activation data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [analyticsDays]);

  const runBatch = async () => {
    if (!window.confirm(`Auto-send activation emails to all ${total} due users? Uses your Gmail SMTP (or Resend fallback).`))
      return;
    setRunning(true);
    try {
      const { data } = await client.post("/admin/activation/run-batch");
      toast.success(`Batch complete: sent ${data.sent}, failed ${data.failed}${data.skipped_draft_errors ? `, ${data.skipped_draft_errors} draft errors` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally { setRunning(false); }
  };

  const skipUser = async (userId, email) => {
    if (!window.confirm(`Permanently exclude ${email} from all activation emails?`)) return;
    try {
      await client.post(`/admin/activation/skip/${userId}`);
      toast.success(`${email} skipped`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skip failed");
    }
  };

  const [nudging, setNudging] = useState({});
  const oneClickNudge = async (userId, email) => {
    if (!window.confirm(`AI-draft and send an activation nudge to ${email} right now?`)) return;
    setNudging((n) => ({ ...n, [userId]: true }));
    try {
      const { data } = await client.post(`/admin/activation/nudge/${userId}`);
      if (data?.status === "sent") {
        const via = data.provider === "gmail_smtp" ? " via your Gmail" :
                    data.provider === "resend" ? " via Resend" : "";
        toast.success(`Sent to ${email}${via}`);
        await load();
      } else {
        toast.error(data?.error || "Nudge failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed");
    } finally {
      setNudging((n) => { const c = { ...n }; delete c[userId]; return c; });
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="activation-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
          <Lightning size={12} weight="fill" /> Admin • Auto-run every hour
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Activation Emails</h1>
        <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
          Automatic nudge emails for users stuck on onboarding steps. Runs every hour in the background.
          Trigger a batch manually or send one-by-one below.
        </p>
      </div>

      {/* Analytics funnel */}
      {analytics && (
        <ActivationAnalytics
          data={analytics}
          days={analyticsDays}
          onDaysChange={setAnalyticsDays}
        />
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-6">
        <div className="card-surface p-4" data-testid="activation-stat-total">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total Due</div>
          <div className="text-3xl font-display font-semibold mt-1">{total}</div>
        </div>
        {Object.entries(STEP_LABELS).map(([key, s]) => (
          <div key={key} className="card-surface p-4" data-testid={`activation-stat-${key}`}>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">{s.short}</div>
            <div className={`text-3xl font-display font-semibold mt-1 ${s.color}`}>{perStep[key] || 0}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button
          onClick={runBatch}
          disabled={running || total === 0}
          data-testid="activation-run-batch-btn"
          className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Play size={14} weight="fill" /> {running ? "Running..." : `Run batch now (${total})`}
        </button>
        <button
          onClick={load}
          disabled={loading}
          data-testid="activation-refresh-btn"
          className="rounded-full px-4 py-2 text-sm border border-white/10 hover:border-white/25 text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-2 disabled:opacity-60"
        >
          <ArrowClockwise size={14} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 mb-4">
        <button
          onClick={() => setTab("due")}
          data-testid="activation-tab-due"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "due" ? "border-[#FF5A1F] text-white" : "border-transparent text-neutral-500 hover:text-white"
          }`}
        >
          Due Now ({total})
        </button>
        <button
          onClick={() => setTab("sent")}
          data-testid="activation-tab-sent"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "sent" ? "border-[#FF5A1F] text-white" : "border-transparent text-neutral-500 hover:text-white"
          }`}
        >
          Sent History ({history.length})
        </button>
      </div>

      {/* Content */}
      {tab === "due" ? (
        <div className="card-surface overflow-hidden" data-testid="activation-due-list">
          {candidates.length === 0 ? (
            <div className="p-12 text-center text-neutral-500">
              <CheckCircle size={40} className="mx-auto text-emerald-400 mb-3" />
              <div className="text-lg text-white font-semibold">No users due right now</div>
              <div className="text-sm mt-1">The auto-scheduler will queue new nudges as users cross the delay thresholds.</div>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-neutral-500">
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Signed Up</th>
                  <th className="text-left px-4 py-3">Stuck On</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const s = STEP_LABELS[c.step_key] || {};
                  return (
                    <tr key={`${c.user.id}-${c.step_key}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-white">{c.user.full_name || "—"}</div>
                        <div className="text-xs text-neutral-500">{c.user.email}</div>
                        {c.user.company_name && (
                          <div className="text-xs text-neutral-600 mt-0.5">{c.user.company_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-neutral-300 inline-flex items-center gap-1">
                          <Clock size={12} /> {fmtHours(c.user.hours_since_signup)} ago
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/[0.05] ${s.color}`}>
                          {s.title}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => oneClickNudge(c.user.id, c.user.email)}
                            disabled={!!nudging[c.user.id]}
                            data-testid={`activation-nudge-btn-${c.user.id}`}
                            title="AI-draft + send in one click (warm tone)"
                            className="text-xs rounded-full px-3 py-1.5 bg-[#FF5A1F]/15 border border-[#FF5A1F]/50 text-[#FF9764] hover:bg-[#FF5A1F]/25 hover:text-white transition-colors inline-flex items-center gap-1 disabled:opacity-60"
                          >
                            <Lightning size={11} weight="fill" />
                            {nudging[c.user.id] ? "Sending…" : "Nudge"}
                          </button>
                          <button
                            onClick={() => setModal({ open: true, candidate: c })}
                            data-testid={`activation-draft-btn-${c.user.id}`}
                            className="text-xs rounded-full px-3 py-1.5 border border-[#FF5A1F]/40 text-[#FF5A1F] hover:bg-[#FF5A1F]/10 transition-colors inline-flex items-center gap-1"
                          >
                            <Sparkle size={11} weight="fill" /> Draft
                          </button>
                          <button
                            onClick={() => skipUser(c.user.id, c.user.email)}
                            data-testid={`activation-skip-btn-${c.user.id}`}
                            title="Permanently exclude from activation emails"
                            className="text-xs rounded-full px-2.5 py-1.5 border border-white/10 text-neutral-500 hover:text-white hover:border-white/25 transition-colors"
                          >
                            <EyeSlash size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="card-surface overflow-hidden" data-testid="activation-history-list">
          {history.length === 0 ? (
            <div className="p-12 text-center text-neutral-500">
              <EnvelopeSimple size={40} className="mx-auto mb-3" />
              <div className="text-lg text-white font-semibold">No activation emails sent yet</div>
              <div className="text-sm mt-1">Once the batch runs (or you send manually), sent emails will appear here.</div>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-neutral-500">
                  <th className="text-left px-4 py-3">Recipient</th>
                  <th className="text-left px-4 py-3">Step</th>
                  <th className="text-left px-4 py-3">Subject</th>
                  <th className="text-left px-4 py-3">Provider</th>
                  <th className="text-left px-4 py-3">Engagement</th>
                  <th className="text-left px-4 py-3">Sent</th>
                </tr>
              </thead>
              <tbody>
                {history.map((e) => {
                  const s = STEP_LABELS[e.step_key] || {};
                  return (
                    <tr key={e.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="text-sm text-white">{e.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.05] ${s.color}`}>
                          {s.short}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-neutral-300 max-w-md truncate">{e.subject}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${e.status === "sent" ? "text-emerald-400" : "text-red-400"}`}>
                          {e.provider || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-400">
                        {e.open_count > 0 || e.click_count > 0 ? (
                          <span>👁 {e.open_count || 0} • ↗ {e.click_count || 0}</span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-500">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <ComposeModal
        open={modal.open}
        candidate={modal.candidate}
        adminEmail={user?.email}
        onClose={() => setModal({ open: false, candidate: null })}
        onSent={load}
      />
    </div>
  );
}
